import { Routes } from '@angular/router';

import { CamRoutes } from '@camelot/menu';
import { AuthGuard } from '@camelot/user';

export enum EShowcaseRoute {
  agGrid = 'ag-grid',
  agGridEditabled = 'ag-grid-editabled',
  agGridEditabledQuotation = 'ag-grid-editabled-quotation',
  calendar = 'calendar',
  charts = 'charts',
  communication = 'communication',
  files = 'files',
  forms = 'forms',
  icons = 'icons',
  layout = 'layout',
  maps = 'maps',
  menus = 'menus',
  notifications = 'notifications',
  sandboxWebPush = 'sandbox-web-push',
  showcase = 'showcase',
  theme = 'theme',
  ui = 'ui',
  webPush = 'web-push',
}

CamRoutes.addRoute({
  children: [
    { key: EShowcaseRoute.agGrid, url: 'ag-grid' },
    { key: EShowcaseRoute.agGridEditabled, url: 'ag-grid-editabled' },
    { key: EShowcaseRoute.agGridEditabledQuotation, url: 'ag-grid-editabled-quotation' },
    { key: EShowcaseRoute.calendar, url: 'calendar' },
    { key: EShowcaseRoute.charts, url: 'charts' },
    { key: EShowcaseRoute.communication, url: 'communication' },
    { key: EShowcaseRoute.files, url: 'files' },
    { key: EShowcaseRoute.forms, url: 'forms' },
    { key: EShowcaseRoute.icons, url: 'icons' },
    { key: EShowcaseRoute.layout, url: 'layout' },
    { key: EShowcaseRoute.maps, url: 'maps' },
    { key: EShowcaseRoute.menus, url: 'menus' },
    { key: EShowcaseRoute.notifications, url: 'notifications' },
    { key: EShowcaseRoute.sandboxWebPush, url: 'sandbox-web-push' },
    { key: EShowcaseRoute.theme, url: 'theme' },
    { key: EShowcaseRoute.ui, url: 'ui' },
    { key: EShowcaseRoute.webPush, url: 'web-push' },
  ],
  key: EShowcaseRoute.showcase,
  url: 'showcase',
});

export const showcaseRoutes: Routes = [
  {
    loadComponent: () => import('./pages/showcase/showcase.component').then(c => c.ShowcasePage),
    path: '',
    pathMatch: 'full',
  },
  {
    children: [
      {
        loadComponent: () => import('./pages/ag-grid/basic/basic.component').then(c => c.AgGridBasicPage),
        path: 'basic',
      },
      {
        loadComponent: () => import('./pages/ag-grid/rendering/rendering.component').then(c => c.AgGridRenderingPage),
        path: 'rendering',
      },
      {
        loadComponent: () => import('./pages/ag-grid/controls/controls.component').then(c => c.AgGridControlsPage),
        path: 'controls',
      },
      {
        loadComponent: () => import('./pages/ag-grid/table-view/table-view.component').then(c => c.AgGridTableViewPage),
        path: 'table-view',
      },
      {
        loadComponent: () =>
          import('./pages/ag-grid/navigation/navigation.component').then(c => c.AgGridNavigationPage),
        path: 'navigation',
      },
      {
        loadComponent: () =>
          import('./pages/ag-grid/pagination/pagination.component').then(c => c.AgGridPaginationPage),
        path: 'pagination',
      },
      {
        loadComponent: () => import('./pages/ag-grid/height/height.component').then(c => c.AgGridHeightPage),
        path: 'height',
      },
      {
        loadComponent: () => import('./pages/ag-grid/highlight/highlight.component').then(c => c.AgGridHighlightPage),
        path: 'highlight',
      },
      {
        loadComponent: () =>
          import('./pages/ag-grid/prefilters/prefilters.component').then(c => c.AgGridPreFiltersPage),
        path: 'prefilters',
      },
      {
        loadComponent: () => import('./pages/ag-grid/tree-view/tree-view.component').then(c => c.AgGridTreeViewPage),
        path: 'tree-view',
      },
      {
        canActivate: [AuthGuard],
        loadComponent: () =>
          import('./pages/ag-grid/editabled-data/editabled-data.component').then(c => c.AgGridEditabledDataPage),
        path: 'editabled-data',
      },
      { path: '', pathMatch: 'full', redirectTo: 'basic' },
    ],
    loadComponent: () => import('./pages/ag-grid/ag-grid-shell.component').then(c => c.AgGridShellComponent),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.agGrid]),
  },
  {
    loadComponent: () =>
      import('./pages/ag-grid-editabled/ag-grid-editabled.component').then(c => c.AgGridEditabledPage),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.agGridEditabled]),
    canActivate: [AuthGuard],
  },
  {
    loadComponent: () =>
      import('./pages/ag-grid-editabled-quotation/ag-grid-editabled-quotation.component').then(
        c => c.AgGridEditabledQuotationPage
      ),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.agGridEditabledQuotation]),
    canActivate: [AuthGuard],
  },
  {
    children: [
      {
        loadComponent: () => import('./pages/calendar/basic/basic.component').then(c => c.CalendarBasicPage),
        path: 'basic',
      },
      {
        loadComponent: () =>
          import('./pages/calendar/time-ranges/time-ranges.component').then(c => c.CalendarTimeRangesPage),
        path: 'time-ranges',
      },
      {
        loadComponent: () => import('./pages/calendar/drag-drop/drag-drop.component').then(c => c.CalendarDragDropPage),
        path: 'drag-drop',
      },
      {
        loadComponent: () =>
          import('./pages/calendar/scheduler/scheduler.component').then(c => c.CalendarSchedulerPage),
        path: 'scheduler',
      },
      {
        loadComponent: () => import('./pages/calendar/controls/controls.component').then(c => c.CalendarControlsPage),
        path: 'controls',
      },
      {
        loadComponent: () =>
          import('./pages/calendar/resources/resources.component').then(c => c.CalendarResourcesPage),
        path: 'resources',
      },
      {
        loadComponent: () => import('./pages/calendar/dynamic/dynamic.component').then(c => c.CalendarDynamicPage),
        path: 'dynamic',
      },
      {
        loadComponent: () => import('./pages/calendar/multi/multi.component').then(c => c.CalendarMultiPage),
        path: 'multi',
      },
      {
        loadComponent: () => import('./pages/calendar/views/views.component').then(c => c.CalendarViewsPage),
        path: 'views',
      },
      { path: '', pathMatch: 'full', redirectTo: 'basic' },
    ],
    loadComponent: () => import('./pages/calendar/calendar-shell.component').then(c => c.CalendarShellComponent),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.calendar]),
  },
  {
    loadComponent: () => import('./pages/charts/charts.component').then(c => c.ChartsPage),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.charts]),
  },
  {
    loadComponent: () => import('./pages/communication/communication.component').then(c => c.CommunicationPage),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.communication]),
  },
  {
    loadComponent: () => import('./pages/files/files.component').then(c => c.FilesPage),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.files]),
  },
  {
    children: [
      {
        loadComponent: () => import('./pages/forms/text/text.component').then(c => c.FormsTextPage),
        path: 'text',
      },
      {
        loadComponent: () => import('./pages/forms/number/number.component').then(c => c.FormsNumberPage),
        path: 'number',
      },
      {
        loadComponent: () => import('./pages/forms/contact/contact.component').then(c => c.FormsContactPage),
        path: 'contact',
      },
      {
        loadComponent: () => import('./pages/forms/selection/selection.component').then(c => c.FormsSelectionPage),
        path: 'selection',
      },
      {
        loadComponent: () => import('./pages/forms/choices/choices.component').then(c => c.FormsChoicesPage),
        path: 'choices',
      },
      {
        loadComponent: () => import('./pages/forms/boolean/boolean.component').then(c => c.FormsBooleanPage),
        path: 'boolean',
      },
      {
        loadComponent: () => import('./pages/forms/edit-field/edit-field.component').then(c => c.FormsEditFieldPage),
        path: 'edit-field',
      },
      {
        loadComponent: () => import('./pages/forms/sizes/sizes.component').then(c => c.FormsSizesPage),
        path: 'sizes',
      },
      {
        loadComponent: () => import('./pages/forms/datetime/datetime.component').then(c => c.FormsDatetimePage),
        path: 'datetime',
      },
      {
        loadComponent: () => import('./pages/forms/color/color.component').then(c => c.FormsColorPage),
        path: 'color',
      },
      {
        loadComponent: () => import('./pages/forms/files/files.component').then(c => c.FormsFilesPage),
        path: 'files',
      },
      {
        loadComponent: () => import('./pages/forms/address/address.component').then(c => c.FormsAddressPage),
        path: 'address',
      },
      {
        loadComponent: () => import('./pages/forms/wysiwyg/wysiwyg.component').then(c => c.FormsWysiwygPage),
        path: 'wysiwyg',
      },
      {
        loadComponent: () => import('./pages/forms/dynamic/dynamic.component').then(c => c.FormsDynamicPage),
        path: 'dynamic',
      },
      {
        loadComponent: () => import('./pages/forms/utils/utils.component').then(c => c.FormsUtilsPage),
        path: 'utils',
      },
      {
        loadComponent: () => import('./pages/forms/step-form/step-form.component').then(c => c.FormsStepFormPage),
        path: 'step-form',
      },
      {
        loadComponent: () => import('./pages/forms/all/all.component').then(c => c.FormsAllPage),
        path: 'all',
      },
      { path: '', pathMatch: 'full', redirectTo: 'text' },
    ],
    loadComponent: () => import('./pages/forms/forms-shell.component').then(c => c.FormsShellComponent),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.forms]),
  },
  {
    loadComponent: () => import('./pages/icons/icons.component').then(c => c.IconsPage),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.icons]),
  },
  {
    loadComponent: () => import('./pages/layout/layout.component').then(c => c.LayoutPage),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.layout]),
  },
  {
    loadComponent: () => import('./pages/maps/maps.component').then(c => c.MapsPage),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.maps]),
  },
  {
    children: [
      {
        loadComponent: () => import('./pages/menus/main-menu/main-menu.component').then(c => c.MenusMainMenuPage),
        path: 'main-menu',
      },
      {
        loadComponent: () => import('./pages/menus/menu/menu.component').then(c => c.MenusMenuPage),
        path: 'menu',
      },
      {
        loadComponent: () => import('./pages/menus/navigation/navigation.component').then(c => c.MenusNavigationPage),
        path: 'navigation',
      },
      {
        loadComponent: () =>
          import('./pages/menus/context-menu/context-menu.component').then(c => c.MenusContextMenuPage),
        path: 'context-menu',
      },
      {
        loadComponent: () =>
          import('./pages/menus/quick-actions/quick-actions.component').then(c => c.MenusQuickActionsPage),
        path: 'quick-actions',
      },
      {
        loadComponent: () => import('./pages/menus/toggle/toggle.component').then(c => c.MenusTogglePage),
        path: 'toggle',
      },
      {
        loadComponent: () =>
          import('./pages/menus/bottom-sheet/bottom-sheet.component').then(c => c.MenusBottomSheetPage),
        path: 'bottom-sheet',
      },
      { path: '', pathMatch: 'full', redirectTo: 'main-menu' },
    ],
    loadComponent: () => import('./pages/menus/menus-shell.component').then(c => c.MenusShellComponent),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.menus]),
  },
  {
    loadComponent: () => import('./pages/notifications/notifications.component').then(c => c.NotificationsPage),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.notifications]),
  },
  {
    loadComponent: () => import('./pages/theme/theme.component').then(c => c.ThemePage),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.theme]),
  },
  {
    loadComponent: () => import('./pages/web-push/web-push.component').then(c => c.WebPushPage),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.webPush]),
  },
  {
    loadComponent: () => import('src/app/features/sandbox/pages/web-push/web-push.component').then(c => c.WebPushPage),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.sandboxWebPush]),
  },
  {
    children: [
      {
        loadComponent: () => import('./pages/ui/typography/typography.component').then(c => c.UiTypographyPage),
        path: 'typography',
      },
      {
        loadComponent: () => import('./pages/ui/buttons/buttons.component').then(c => c.UiButtonsPage),
        path: 'buttons',
      },
      { loadComponent: () => import('./pages/ui/badges/badges.component').then(c => c.UiBadgesPage), path: 'badges' },
      { loadComponent: () => import('./pages/ui/cards/cards.component').then(c => c.UiCardsPage), path: 'cards' },
      { loadComponent: () => import('./pages/ui/lists/lists.component').then(c => c.UiListsPage), path: 'lists' },
      { loadComponent: () => import('./pages/ui/user/user.component').then(c => c.UiUserPage), path: 'user' },
      {
        loadComponent: () => import('./pages/ui/messages/messages.component').then(c => c.UiMessagesPage),
        path: 'messages',
      },
      {
        loadComponent: () => import('./pages/ui/progress/progress.component').then(c => c.UiProgressPage),
        path: 'progress',
      },
      { loadComponent: () => import('./pages/ui/stats/stats.component').then(c => c.UiStatsPage), path: 'stats' },
      { loadComponent: () => import('./pages/ui/states/states.component').then(c => c.UiStatesPage), path: 'states' },
      {
        loadComponent: () => import('./pages/ui/expansion/expansion.component').then(c => c.UiExpansionPage),
        path: 'expansion',
      },
      {
        loadComponent: () => import('./pages/ui/display/display.component').then(c => c.UiDisplayPage),
        path: 'display',
      },
      {
        loadComponent: () => import('./pages/ui/placeholder/placeholder.component').then(c => c.UiPlaceholderPage),
        path: 'placeholder',
      },
      { path: '', pathMatch: 'full', redirectTo: 'typography' },
    ],
    loadComponent: () => import('./pages/ui/ui-shell.component').then(c => c.UiShellComponent),
    path: CamRoutes.getUrl([EShowcaseRoute.showcase, EShowcaseRoute.ui]),
  },
];
